import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import * as signalR from "@microsoft/signalr";

// 🧠 SINGLETONS (Outside component to persist across re-renders)
let globalConnection = null;
let globalCurrentToken = ""; // 🔑 The fix: Mutable token storage

const chartURL = "https://chart.tradex247.com/";
const sToken =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1bmlxdWVfbmFtZSI6IlVSMTEyIiwicm9sZSI6IkNoYXJ0VXNlciIsImV4Y2hhbmdlcyI6IiIsInVpZCI6IiIsIm5iZiI6MTc3Nzg3MDU4OCwiZXhwIjoxNzc3OTMyMDAwLCJpYXQiOjE3Nzc4NzA1ODh9.Kd3zecby_0Fcqxux5UMdUWMfnBHKOjczYbfRHaVQftU";

const MarketWatchContext = createContext();
export const useChat = () => useContext(MarketWatchContext);

export const MarketWatchProvider = ({ children }) => {
  const [connected, setConnected] = useState(false);
  const connectingRef = useRef(false);

  // 1️⃣ SYNC TOKEN: Update global variable immediately when Redux changes
  if (sToken) globalCurrentToken = sToken;

  useEffect(() => {
    if (!chartURL || !sToken) return;

    // 2️⃣ REUSE LOGIC: If connection exists, just ensure it's on
    if (globalConnection) {
      if (globalConnection.state === signalR.HubConnectionState.Connected) {
        setConnected(true);
      } else if (
        globalConnection.state === signalR.HubConnectionState.Disconnected
      ) {
        startConnection(globalConnection);
      }
      return;
    }

    // 3️⃣ INIT LOGIC: Create only if missing
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${chartURL}markethub`, {
        // 🚀 CRITICAL FIX: Read from global variable, not closure
        accessTokenFactory: () => globalCurrentToken,
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 20000]) // Progressive backoff
      .configureLogging(signalR.LogLevel.Warning) // Keep console clean in Prod
      .build();

    globalConnection = connection;

    // --- Life Cycle Handlers ---
    connection.onreconnecting(() => setConnected(false));
    connection.onreconnected(() => setConnected(true));
    connection.onclose(async () => {
      setConnected(false);
      // Optional: Force a hard restart logic here if needed
    });

    startConnection(connection);

    // Cleanup: We intentionally DO NOT stop the connection on unmount
    // to preserve the Singleton pattern across page navigation.
    return () => {};
  }, [chartURL, sToken]);

  // --- Robust Start Helper ---
  const startConnection = async (connection) => {
    // Prevent race conditions
    if (
      connectingRef.current ||
      !connection ||
      connection.state === signalR.HubConnectionState.Connected ||
      connection.state === signalR.HubConnectionState.Connecting
    )
      return;

    connectingRef.current = true;
    try {
      await connection.start();
      console.log("✅ MarketWatch Connected");
      setConnected(true);
    } catch (err) {
      console.warn("⚠️ MarketWatch Connect Failed:", err.message);
      setConnected(false);
      // Retry logic is handled by 'withAutomaticReconnect' or global interval if needed
    } finally {
      connectingRef.current = false;
    }
  };

  // --- Browser Focus/Network Handlers ---
  useEffect(() => {
    if (!globalConnection) return;

    const handleVisibility = () => {
      if (
        document.visibilityState === "visible" &&
        globalConnection.state === signalR.HubConnectionState.Disconnected
      ) {
        startConnection(globalConnection);
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("online", () => startConnection(globalConnection));

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("online", () => {});
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      connected,
      on: (evt, cb) => globalConnection?.on(evt, cb),
      off: (evt, cb) => globalConnection?.off(evt, cb),
      send: (method, ...args) =>
        globalConnection
          ?.invoke(method, ...args)
          .catch((e) => console.error(e)),
    }),
    [connected],
  );

  return (
    <MarketWatchContext.Provider value={contextValue}>
      {children}
    </MarketWatchContext.Provider>
  );
};
